import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

/**
 * Aide CONTEXTUELLE poussée par une page vers le panneau d'aide global (HelpPanel), 28/10/2026.
 *
 * Une page qui sait mieux que sa route ce dont l'utilisateur a besoin (ex. : la ligne
 * sélectionnée dans le rapport Conformité) appelle :
 *
 *   const ouvrirAide = usePousserAide(aide);   // aide = null → aide générale de la route
 *
 * aide = {
 *   cle,                       // identifiant stable (ex. 'conformite:1.1')
 *   titre, sousTitre?,         // en tête du bloc
 *   sections: [{ heading, content?, items? }],   // texte BRUT (content) ou liste (items)
 *   quitter?,                  // () => void : « revenir à l'aide générale » (la page désélectionne)
 * }
 *
 * Le contenu est retiré quand la page le remplace par null ou se démonte. `ouvrirAide()`
 * ouvre le panneau. Hors du fournisseur (tests d'une page seule, pages autonomes), le hook ne
 * fait rien et ne plante pas.
 *
 * Deux contextes : les ACTIONS (stables) pour les pages, la VALEUR pour le panneau — pousser une
 * aide ne re-rend donc pas la page qui la pousse.
 */
const ActionsCtx = createContext(null);
const ValeurCtx = createContext(null);
const RIEN = { aide: null, ouverture: 0 };

export function AideContextuelleProvider({ children }) {
  const [aide, setAide] = useState(null);
  const [ouverture, setOuverture] = useState(0);
  const actions = useMemo(() => ({
    pousser: (a) => setAide(a || null),
    demanderOuverture: () => setOuverture((n) => n + 1),
  }), []);
  const valeur = useMemo(() => ({ aide, ouverture }), [aide, ouverture]);
  return (
    <ActionsCtx.Provider value={actions}>
      <ValeurCtx.Provider value={valeur}>{children}</ValeurCtx.Provider>
    </ActionsCtx.Provider>
  );
}

/** Côté panneau : { aide, ouverture } (ouverture = compteur des demandes d'ouverture). */
export function useAideContextuelle() {
  return useContext(ValeurCtx) || RIEN;
}

const RIEN_A_OUVRIR = () => {};

/** Côté page : pousse `aide` (objet mémoïsé par la page, ou null) et rend `ouvrirAide()`. */
export function usePousserAide(aide) {
  const actions = useContext(ActionsCtx);
  useEffect(() => {
    if (!actions) return undefined;
    actions.pousser(aide);
    return () => actions.pousser(null);
  }, [actions, aide]);
  return actions ? actions.demanderOuverture : RIEN_A_OUVRIR;
}
